'use client'

import React, { useMemo } from 'react'
import { Button } from '@mantine/core'
import { Sku } from '@/types/interfaces/sku'
import { ServiceItem } from '@/types/interfaces/service-item'
import { SkuDetailCta } from '@/components/SkuDetailCta'
import { RefreshCtaButton, type CtaVariant } from './RefreshCtaButton'
import { useGetSkuQuery } from '@/lib/store/api/skuApi'
import { PageLocation } from '@nile/mantine-theme/analytics'
import type { SkuCtaVariant } from '@/components/SkuDetailCta.styles'
import { getSkuCtaStyles } from '@/components/SkuDetailCta.styles'

/** Theme names used by Refresh category pages (excludes 'detail'). */
export type SkuGetStartedCtaTheme = Exclude<SkuCtaVariant, 'detail'>

interface SkuGetStartedCtaProps {
    sku?: Sku | null
    service?: ServiceItem | null
    theme?: SkuGetStartedCtaTheme
    pageLocation?: PageLocation
    additionalInformation?: Record<string, unknown>
    label?: string
    buttonSize?: 'lg' | 'md'
    className?: string
    style?: React.CSSProperties
    fallbackHref?: string
    fallbackLabel?: string
    /**
     * Button variant to use when rendering the fallback link (no resolved SKU/service).
     * Defaults to the themed mapping; set explicitly to opt into a specific variant
     * without affecting other callers.
     */
    fallbackVariant?: CtaVariant
    skuPageVariant?: 'refresh' | 'legacy'
    prefetch?: boolean
}

export function SkuGetStartedCta({
    sku: skuProp,
    service,
    theme = 'primary',
    pageLocation = PageLocation.CATEGORY,
    additionalInformation = {},
    label,
    buttonSize = 'md',
    className,
    style,
    fallbackHref,
    fallbackLabel,
    fallbackVariant,
    skuPageVariant,
    prefetch,
}: SkuGetStartedCtaProps) {
    const defaultFallbackVariant: CtaVariant = theme === 'outline-dark' ? 'outline-dark' : 'primary'
    const resolvedFallbackVariant = fallbackVariant ?? defaultFallbackVariant
    const skuId =
        skuProp?.id ??
        service?.skuId?.toString() ??
        (service as { sku_id?: string })?.sku_id?.toString()
    const { data: fetchedSku, isLoading: skuLoading } = useGetSkuQuery(skuId ?? '', {
        skip: !skuId || !!skuProp,
    })

    const sku = skuProp ?? (service && fetchedSku ? fetchedSku : null)
    const themeStyle = getSkuCtaStyles(theme)

    const displayLabel = useMemo(() => {
        if (label) return label
        if (sku?.category === 'lab') return 'Book your test now'
        return 'Get started'
    }, [label, sku?.category])

    if (!service && !skuProp) {
        if (fallbackHref && fallbackLabel) {
            return (
                <RefreshCtaButton href={fallbackHref} variant={resolvedFallbackVariant} prefetch={prefetch}>
                    {fallbackLabel}
                </RefreshCtaButton>
            )
        }
        return null
    }

    if (!sku) {
        if (skuLoading) {
            return (
                <Button
                    loading
                    disabled
                    variant={themeStyle.variant === 'filled' ? 'filled' : 'outline'}
                    bg={themeStyle.bg}
                    c={themeStyle.c}
                    px={24}
                    py={16}
                    radius={999}
                    size="sm"
                    fw={550}
                    lh={1.4}
                    h={48}
                    styles={{ root: { minHeight: 48, ...themeStyle.rootStyles } }}
                >
                    {displayLabel}
                </Button>
            )
        }
        if (fallbackHref && fallbackLabel) {
            return (
                <RefreshCtaButton href={fallbackHref} variant={resolvedFallbackVariant} prefetch={prefetch}>
                    {fallbackLabel}
                </RefreshCtaButton>
            )
        }
        return null
    }

    return (
        <SkuDetailCta
            sku={sku}
            variant={theme}
            label={label}
            buttonSize={buttonSize}
            pageLocation={pageLocation}
            additionalInformation={additionalInformation}
            skuPageVariant={skuPageVariant}
            className={className}
            style={style}
        />
    )
}
